import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonColor, ButtonSize } from '@widgets/eop-button/eop-button.component';
import { EopButtonModule } from '@widgets/eop-button/eop-button.module';
import { IconColor } from '@widgets/eop-icon/eop-icon.component';
import { EopIconModule } from '@widgets/eop-icon/eop-icon.module';
import { ModalService } from '@widgets/modal/modal.service';
import { PacmanComponent } from '@widgets/pacman/pacman.component';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface DownloadProgress {
  progress: number;
  progressText: string;
  downloadId: string;
  fileName: string;
  downloadButtonText: string;
  finished: boolean;
  failed: boolean;
}

export interface DownloadData {
  downloadId: string;
  fileName: string;
}

@Component({
  selector: 'eop-download-progress',
  imports: [CommonModule, EopIconModule, TranslateModule, EopButtonModule],
  templateUrl: './download-progress.component.html',
  styleUrl: './download-progress.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class DownloadProgressComponent implements OnInit, OnDestroy {
  data = inject<{
    progress$: BehaviorSubject<DownloadProgress>;
    download$: Subject<DownloadData>;
  }>(MAT_SNACK_BAR_DATA);
  private readonly snackBarRef = inject<MatSnackBarRef<DownloadProgressComponent>>(MatSnackBarRef);
  private readonly modalService = inject(ModalService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly ButtonColor = ButtonColor;
  protected readonly ButtonSize = ButtonSize;
  protected readonly IconColor = IconColor;

  private readonly unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.data.progress$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.cdr.detectChanges();
    });
    this.data.download$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.data.progress$.next(null);
    this.data.progress$.complete();
    this.data.download$.next(null);
    this.data.download$.complete();
  }

  closeSnackbar() {
    this.snackBarRef.dismiss();
  }

  downloadButtonClicked() {
    this.data.download$.next({
      downloadId: this.data.progress$.value.downloadId,
      fileName: this.data.progress$.value.fileName,
    });
    this.snackBarRef.dismiss();
  }

  openPacman() {
    this.modalService.open(PacmanComponent, {});
  }
}
